import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ShieldCheck,
  RefreshCw,
  Clock,
  Calendar,
  ExternalLink,
  Info,
  CheckCircle2,
} from "lucide-react";
import { usePageSeo } from "@/lib/pageSeo";
import { track } from "@/lib/analytics";

/**
 * /reserve — Expression of Interest (EOI) booking page.
 *
 * For buyers who cannot travel to the on-site launch on 12-13 September 2026
 * but want to lock Chapter III launch pricing until Dussehra (20 October 2026)
 * with a fully-refundable token amount.
 *
 * The actual EOI application form lives on the group's central portal at
 * https://www.dquorspaces.co/eoi-application — this page frames the offer,
 * sets expectations for the outbound form, and CTAs into it in a new tab.
 */

const EOI_URL = "https://www.dquorspaces.co/eoi-application";

const trackEoiClick = (source) => {
  try {
    track("cta_click", { cta: "eoi_apply", source });
  } catch {
    /* analytics never blocks CTAs */
  }
};

export default function Reserve() {
  usePageSeo({
    title: "Reserve Your Chapter III Plot Online · Lock Launch Pricing Until Dussehra — Pali Hill City",
    description:
      "Can't travel to the Sept 12-13 launch? Lock Chapter III launch pricing (₹1,990/sqft) until Dussehra, 20 October 2026, with a fully-refundable EOI. Silver or Gold tier. No deductions for 6 months from launch.",
    canonical: "https://palihillcity.com/reserve",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Offer",
      name: "Pali Hill City Chapter III — Refundable Online EOI",
      description:
        "Refundable Expression of Interest (Silver or Gold tier) that locks Chapter III launch pricing until Dussehra, 20 October 2026. Fully refundable with no deductions for six months from launch.",
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
      validFrom: "2026-08-01",
      validThrough: "2026-10-20",
      seller: { "@type": "Organization", name: "DQUOR Spaces LLP" },
      url: "https://palihillcity.com/reserve",
    },
  });

  return (
    <div data-testid="reserve-page">
      {/* Hero */}
      <section className="section-pad pb-8 bg-offwhite pt-32">
        <div className="container-editorial grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <div className="eyebrow text-teal mb-4">
              <span className="editorial-rule" /> Reserve Online
            </div>
            <h1 className="font-display text-forest text-5xl md:text-6xl leading-[0.98]">
              Can&apos;t make it to<br />
              the launch weekend?<br />
              Lock the price anyway.
            </h1>
            <p className="mt-6 text-forest/70 text-lg leading-relaxed max-w-2xl">
              Chapter III opens at ₹1,990/sqft on-site on 12–13 September 2026. If
              travel doesn&apos;t work, place a{" "}
              <strong className="text-forest">fully-refundable Expression of
              Interest</strong> now and hold the launch price until Dussehra —
              20 October 2026.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={EOI_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEoiClick("hero")}
                data-testid="reserve-hero-cta"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-forest text-cream font-body text-base hover:bg-void transition-colors group"
              >
                Reserve online with a refundable EOI
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
              <Link
                to="/event"
                data-testid="reserve-attend-instead-cta"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-forest/20 text-forest font-body text-base hover:bg-forest/5 transition-colors"
              >
                Or attend the launch instead
              </Link>
            </div>

            {/* Inline disclaimer chip */}
            <div
              data-testid="reserve-disclaimer-inline"
              className="mt-6 flex items-start gap-3 text-sm text-forest/70 bg-forest/5 rounded-lg p-4 max-w-2xl leading-relaxed"
            >
              <Info size={16} className="mt-0.5 flex-shrink-0 text-teal" />
              <div>
                The button above opens{" "}
                <span className="font-body text-forest">
                  dquorspaces.co/eoi-application
                </span>{" "}
                in a new tab. There you&apos;ll complete a short payment form, pick{" "}
                <strong>Pali Hill City</strong> as the project and choose your
                EOI tier — <strong>Silver</strong> or <strong>Gold</strong>. Your EOI is
                fully refundable with <em>no deductions</em> for a period of up
                to 6 months from the launch event date.
              </div>
            </div>
          </div>

          {/* Right column — the offer summary card */}
          <aside className="md:col-span-5">
            <div className="border border-forest/15 rounded-lg p-6 bg-white sticky top-28">
              <div className="eyebrow text-teal mb-3">The offer, plain</div>
              <div className="space-y-5">
                <Row
                  icon={<Calendar size={18} />}
                  k="Launch date"
                  v="12–13 September 2026, on-site"
                />
                <Row
                  icon={<Clock size={18} />}
                  k="Price lock valid until"
                  v="Dussehra · 20 October 2026"
                />
                <Row
                  icon={<ShieldCheck size={18} />}
                  k="EOI status"
                  v="Fully refundable · No deductions"
                />
                <Row
                  icon={<RefreshCw size={18} />}
                  k="Refund window"
                  v="Up to 6 months from launch event"
                />
                <Row
                  icon={<CheckCircle2 size={18} />}
                  k="Tiers available"
                  v="Silver · Gold"
                />
              </div>
              <a
                href={EOI_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEoiClick("summary_card")}
                data-testid="reserve-summary-cta"
                className="mt-7 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-forest text-cream font-body text-sm hover:bg-void transition-colors"
              >
                Apply for EOI
                <ExternalLink size={14} />
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* How the EOI works */}
      <section className="section-pad bg-offwhite pt-8">
        <div className="container-editorial">
          <div className="eyebrow text-teal mb-4">
            <span className="editorial-rule" /> How the EOI works
          </div>
          <h2 className="font-display text-forest text-3xl md:text-4xl leading-tight max-w-3xl">
            Four steps. Fully reversible.
          </h2>
          <div className="mt-12 grid md:grid-cols-4 gap-6">
            {[
              {
                n: "01",
                t: "Open the EOI form",
                b: "The button opens dquorspaces.co/eoi-application in a new tab — this is the DQUOR Spaces central EOI portal used across all group projects.",
              },
              {
                n: "02",
                t: "Pick Pali Hill City · pick a tier",
                b: "Select Pali Hill City as the project. Choose Silver or Gold tier depending on how much price-lock and priority you want at launch inventory allocation.",
              },
              {
                n: "03",
                t: "Pay the refundable token",
                b: "Complete the payment form on the DQUOR portal. Standard cards / UPI / net-banking accepted. Confirmation lands in your inbox within minutes.",
              },
              {
                n: "04",
                t: "Launch price locked until Dussehra",
                b: "Your Chapter III launch price (₹1,990/sqft) is held until 20 October 2026. If you change your mind any time within 6 months of launch, you get 100% of the EOI back — no deductions.",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="border border-forest/15 rounded-lg p-6 bg-white"
                data-testid={`reserve-step-${s.n}`}
              >
                <div className="eyebrow text-teal">{s.n}</div>
                <h3 className="font-display text-forest text-xl mt-3 leading-tight">
                  {s.t}
                </h3>
                <p className="mt-3 text-sm text-forest/70 leading-relaxed">
                  {s.b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed disclaimer / T&C block */}
      <section className="section-pad bg-offwhite">
        <div className="container-editorial grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow text-teal mb-4">
              <span className="editorial-rule" /> The fine print, in plain words
            </div>
            <h2 className="font-display text-forest text-3xl md:text-4xl leading-tight">
              What you should<br />know before you click.
            </h2>
          </div>
          <div className="md:col-span-8">
            <div
              data-testid="reserve-disclaimer-block"
              className="border border-forest/15 rounded-lg p-8 bg-white space-y-5 text-[15px] text-forest/80 leading-relaxed"
            >
              <p>
                <strong className="text-forest">The EOI form is on a
                separate portal.</strong>{" "}
                Clicking the button opens{" "}
                <span className="font-body text-forest">
                  dquorspaces.co/eoi-application
                </span>{" "}
                in a new browser tab. This is DQUOR Spaces LLP&apos;s central EOI
                portal — the same portal used across every Malpani Group
                project.
              </p>
              <p>
                <strong className="text-forest">You choose the project
                and the tier.</strong>{" "}
                On the DQUOR portal, select{" "}
                <em>Pali Hill City</em> as the project and pick either{" "}
                <strong>Silver</strong> or <strong>Gold</strong> EOI. The tier
                you pick determines the size of your refundable token and your
                priority position in the Chapter III inventory allocation on
                launch weekend.
              </p>
              <p>
                <strong className="text-forest">
                  Your EOI locks the Chapter III launch price until Dussehra.
                </strong>{" "}
                Chapter III opens at ₹1,990/sqft on 12–13 September 2026. A
                valid EOI holds that same price for you until{" "}
                <strong>Dussehra, 20 October 2026</strong> — even if inventory
                pricing has moved by then.
              </p>
              <p>
                <strong className="text-forest">Fully refundable · no
                deductions · six-month window.</strong>{" "}
                If you decide not to proceed with the plot, your EOI is refunded
                in full — with{" "}
                <em>no deductions of any kind</em> — for a period of up to{" "}
                <strong>6 months from the launch event date</strong>. Refund is
                processed to your original payment method within standard bank
                timelines after your written intimation to{" "}
                <a
                  href="mailto:sales@dquorspaces.co"
                  className="underline underline-offset-4 text-forest"
                >
                  sales@dquorspaces.co
                </a>
                .
              </p>
              <p>
                <strong className="text-forest">Questions?</strong> Call{" "}
                <a
                  href="tel:+918446448123"
                  className="underline underline-offset-4 text-forest"
                >
                  +91 844 644 8123
                </a>{" "}
                or WhatsApp before you apply — the team will walk you through
                Silver vs Gold and the exact token amounts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-pad bg-forest text-cream text-center pt-20 pb-20">
        <div className="max-w-2xl mx-auto px-5">
          <div className="eyebrow text-lime mb-4">Chapter III · Reserve online</div>
          <h3 className="font-display text-4xl md:text-5xl leading-tight text-white">
            Two months of price certainty.<br />One refundable click.
          </h3>
          <p className="mt-6 text-cream/80 text-lg leading-relaxed max-w-xl mx-auto">
            Lock ₹1,990/sqft until Dussehra. Change your mind any time in the
            next six months. No deductions, no drama.
          </p>
          <div className="mt-10">
            <a
              href={EOI_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEoiClick("footer")}
              data-testid="reserve-footer-cta"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-lime text-forest font-body text-base hover:bg-white transition-colors"
            >
              Apply for a refundable EOI
              <ArrowUpRight size={18} />
            </a>
          </div>
          <div className="mt-6 text-cream/60 text-xs">
            Opens dquorspaces.co/eoi-application in a new tab
          </div>
        </div>
      </section>
    </div>
  );
}

function Row({ icon, k, v }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-1 text-forest/70 flex-shrink-0">{icon}</span>
      <div className="flex-1">
        <div className="text-xs uppercase tracking-widest text-forest/50 font-body">
          {k}
        </div>
        <div className="text-forest font-body text-[15px] mt-0.5 leading-snug">
          {v}
        </div>
      </div>
    </div>
  );
}
